import React from "react";
import * as MuiIcons from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Tooltip,
	Typography
} from "@mui/material";
import {ModeEditOutlineOutlined, Visibility, FactCheck, AddCircleOutline} from "@mui/icons-material";
import {Link, useNavigate} from "react-router-dom";
import {helper} from "../../../utils/helper";
import {getPicto} from "../../../utils/pictoCatalog.js";
import ConteneurStatusBadge from "../layout/ConteneurStatusBadge";
import TableHeader from "../layout/table/TableHeader";
import Constants from "../../../utils/constants";
import {isAdmin, isExploitant} from "../../../utils/roles";
import ConteneurConsignesDialog from "./ConteneurConsignesDialog.js";

// Chips cliquables : ouvrent la gestion des consignes du conteneur (ajout d'une 2e consigne, retrait…)
function ConsigneChips({consignes = [], onClick}) {
	if (!consignes.length) {
		return <Typography variant="caption" color="textSecondary">—</Typography>;
	}
	const shown = consignes.slice(0, 2);
	const extra = consignes.length - shown.length;
	return (
	  <Tooltip title={onClick ? "Gérer les consignes" : ""}>
	  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
		  {shown.map((cc) => {
			  const entry = getPicto(cc.consigne?.picto);
			  const Cmp = MuiIcons[entry.mui] || MuiIcons.Help;
			  return (
				<Chip
				  key={cc.id}
				  size="small"
				  icon={<Cmp style={{color: cc.consigne?.couleur || "inherit"}}/>}
				  label={cc.consigne?.libelleFr}
				  variant="outlined"
				  sx={{maxWidth: 150}}
				  onClick={onClick}
				/>
			  );
		  })}
		  {extra > 0 && <Chip size="small" label={`+${extra}`} variant="outlined" onClick={onClick}/>}
	  </Stack>
	  </Tooltip>
	);
}

export default function PatrimoineDetailListV2({conteneurs, patrimoine, patrimoineId, onRefresh}) {
	const navigate = useNavigate();
	const [order, setOrder] = React.useState("asc");
	const [orderBy, setOrderBy] = React.useState("ordreCsv");
	const [selected, setSelected] = React.useState(null);

	const canManage = isAdmin() || isExploitant();

	const headCells = [
		{id: "ordreCsv", label: "#"},
		{id: "nomenclature", label: "Conteneur"},
		{id: "quantite", label: "Bacs"},
		{id: "dechetType", label: "Déchet"},
		{id: "lastInterventionDate", label: "Dernier lavage"},
		{id: "status", label: "Statut"},
		...(canManage ? [{id: "consignes", label: "Consignes"}] : []),
	];

	const getItemComparator = (item, comparator) => {
		if (comparator === "lastInterventionDate") return new Date(item.lastInterventionDate).getTime();
		if (comparator === "ordreCsv") return parseInt(item[comparator], 10);
		return item[comparator];
	};

	const sortedData = () => [...(conteneurs || [])].sort((a, b) => {
		const va = getItemComparator(a, orderBy);
		const vb = getItemComparator(b, orderBy);
		if (order === "asc") return va > vb ? 1 : vb > va ? -1 : 0;
		return va < vb ? 1 : vb < va ? -1 : 0;
	});

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const goDetail = (cId) => navigate(Constants.APP_ROUTE_INTERVENTIONS.replace(":id", cId));

	return (
	  <>
		  <TableContainer component={Paper}>
			  <Table size="small" aria-label="conteneurs">
				  <TableHeader headCells={headCells} order={order} orderBy={orderBy} onRequestSort={handleRequestSort}/>
				  <TableBody>
					  {conteneurs && sortedData().map((c) => {
						  const cId = c["@id"].split("/").pop();
						  return (
							<TableRow hover key={cId} sx={{cursor: "pointer"}} onClick={() => goDetail(cId)}>
								<TableCell sx={{color: "text.secondary", width: 40}}>{c.ordreCsv}</TableCell>
								<TableCell>
									<Typography variant="body2" sx={{fontWeight: 500}}>{c.nomenclature}</Typography>
									<Typography variant="caption" color="textSecondary">
										{[c.adresse, c.commune, c.conteneurType].filter(Boolean).join(" · ")}
									</Typography>
								</TableCell>
								<TableCell>{c.quantite}</TableCell>
								<TableCell>
									<Chip size="small" label={c.dechetType}
									      sx={helper.getColorForDechetType(c.dechetType)}/>
								</TableCell>
								<TableCell>{helper.formatDate(c.lastInterventionDate)}</TableCell>
								<TableCell><ConteneurStatusBadge status={c.status}/></TableCell>

								{canManage &&
								  <TableCell onClick={(e) => e.stopPropagation()}>
									  {(c.consignesActives && c.consignesActives.length > 0)
										? <ConsigneChips consignes={c.consignesActives}
										                 onClick={() => setSelected(c)}/>
										: <Button size="small" color="inherit" startIcon={<AddCircleOutline/>}
										          sx={{color: "text.secondary", textTransform: "none"}}
										          onClick={() => setSelected(c)}>Ajouter</Button>}
								  </TableCell>}

								<TableCell align="right" onClick={(e) => e.stopPropagation()}
								           sx={{whiteSpace: "nowrap"}}>
									{canManage &&
									  <Tooltip title="Consignes">
										  <IconButton size="small"
										              onClick={() => setSelected(c)}><FactCheck/></IconButton>
									  </Tooltip>}
									<Tooltip title="Détail">
										<IconButton size="small" color="primary" component={Link}
										            to={Constants.APP_ROUTE_INTERVENTIONS.replace(":id", cId)}>
											<Visibility/>
										</IconButton>
									</Tooltip>
									{canManage &&
									  <Tooltip title="Éditer">
										  <IconButton size="small" component={Link} color="secondary"
										              to={Constants.APP_ROUTE_CONTENEUR_EDIT.replace(":patrimoineId", patrimoineId).replace(":conteneurId", cId)}>
											  <ModeEditOutlineOutlined/>
										  </IconButton>
									  </Tooltip>}
								</TableCell>
							</TableRow>
						  );
					  })}
				  </TableBody>
			  </Table>
		  </TableContainer>

		  <ConteneurConsignesDialog
			open={Boolean(selected)}
			conteneur={selected}
			onClose={() => setSelected(null)}
			onChanged={onRefresh}
		  />
	  </>
	);
}
